import { recoverTerminalReturn } from './TerminalReturnService';
import type { ScreenType } from '../types/screens';
import { logger } from '../utils/logger';
import { awaitsRememberedResult } from '../utils/pendingEngineResult';
import { runtimeConfig } from './RuntimeConfigService';
import { isFaceMatchSelfieRetry, isFaceOnlyComparison } from './faceMatchRecovery';
import { clearBestSelfie } from '../utils/selfieStorage';

export type RecoveryAction =
  | 'CONTINUE'
  | 'RETRY_OPERATION'
  | 'START_SUCCESSOR_ATTEMPT'
  | 'WAIT'
  | 'START_NEW_SESSION'
  | 'RETURN'
  | 'NONE';

export type WorkflowStage =
  | 'METHOD_SELECTION'
  | 'DEVICE_SELECTION'
  | 'FACE_CAPTURE'
  | 'FACE_PROCESSING'
  | 'SELFIE_HANDOFF'
  | 'DOCUMENT_CONSENT'
  | 'DOCUMENT_CAPTURE'
  | 'DOCUMENT_PROCESSING'
  | 'EXTERNAL_VERIFICATION';

export interface LifecycleProjection {
  sessionId: string;
  sessionStatus: 'PENDING' | 'VERIFIED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
  lifecycleVersion: number;
  expiresAt: string;
  terminal: boolean;
  actor?: {
    role: 'DESKTOP_OBSERVER' | 'MOBILE_OWNER' | null;
  };
  attempt: null | {
    id: string;
    generation: number;
    version: number;
    status: string;
    stage: WorkflowStage;
    operation: null | {
      id: string;
      kind: string;
      generation: number;
      status: string;
      retryCount: number;
      retryLimit: number;
      deadlineAt: string | null;
      /** Portal can still record Verify Engine's result. Absent from older Portal revisions. */
      acceptsResult?: boolean;
    };
  };
  recovery: {
    action: RecoveryAction;
    reason: string;
    targetStage: WorkflowStage | null;
    pollAfterMs: number | null;
    /**
     * Portal-owned: the step stopped for a technical reason, not because of
     * the capture. Absent from older Portal revisions.
     */
    interrupted?: boolean;
  };
  /**
   * Portal-owned: a valid Emblem pass was below the required level. Unlike the
   * step-up reason it holds for the whole session, so the upgraded-pass
   * enrolment offer survives reload during capture and on success. Absent
   * from older Portal revisions.
   */
  emblemUpgradeRequired?: boolean;
  /**
   * Portal-owned face-match recovery for the current attempt.
   * SELFIE_RETRY_OFFERED marks a face-match failure awaiting the customer's
   * choice (another selfie, or optionally a different ID); SELFIE_RETRY marks
   * the new selfie in progress. Absent from older Portal revisions.
   */
  faceMatchRecovery?: FaceMatchRecovery | null;
  destination: {
    returnUrl: string | null;
    cancelUrl: string | null;
    merchantName: string | null;
  };
  successorSession?: { sessionId: string; verifyUrl: string };
}

export type FaceMatchRecovery = 'SELFIE_RETRY_OFFERED' | 'SELFIE_RETRY';

export interface LifecycleOperationFence {
  sessionId: string;
  expectedLifecycleVersion: number;
  attemptId: string;
  attemptGeneration: number;
  expectedAttemptVersion: number;
  operationId: string;
  operationGeneration: number;
}

export const operationFenceFromProjection = (
  projection: LifecycleProjection,
): LifecycleOperationFence | null => {
  const attempt = projection.attempt;
  const operation = attempt?.operation;
  if (!attempt || !operation) return null;

  return {
    sessionId: projection.sessionId,
    expectedLifecycleVersion: projection.lifecycleVersion,
    attemptId: attempt.id,
    attemptGeneration: attempt.generation,
    expectedAttemptVersion: attempt.version,
    operationId: operation.id,
    operationGeneration: operation.generation,
  };
};

const projectionOwnsFence = (
  projection: LifecycleProjection,
  fence: LifecycleOperationFence,
): boolean =>
  projection.sessionId === fence.sessionId &&
  projection.attempt?.id === fence.attemptId &&
  projection.attempt.generation === fence.attemptGeneration &&
  projection.attempt.operation?.id === fence.operationId &&
  projection.attempt.operation.generation === fence.operationGeneration;

type CommandType =
  | 'START_ATTEMPT'
  | 'ADVANCE'
  | 'RETRY_OPERATION'
  | 'REPORT_OPERATION_FAILURE'
  | 'START_SUCCESSOR_ATTEMPT'
  | 'CANCEL'
  | 'START_NEW_SESSION';

const lifecycleLogger = logger.clone({ labels: { component: 'VerificationLifecycle' } });

/**
 * Portal recovery reason for a pending session whose newest Emblem assertion
 * proved a valid credential below the required level. Durable server state,
 * not the callback URL, selects the step-up screen on callback, reload and
 * restoration.
 */
export const EMBLEM_UPGRADE_REQUIRED_REASON = 'EMBLEM_UPGRADE_REQUIRED';

export const isEmblemUpgradeProjection = (projection: LifecycleProjection): boolean =>
  projection.sessionStatus === 'PENDING' &&
  !projection.terminal &&
  projection.recovery.action === 'CONTINUE' &&
  projection.recovery.reason === EMBLEM_UPGRADE_REQUIRED_REASON &&
  (projection.recovery.targetStage || projection.attempt?.stage || 'METHOD_SELECTION') ===
    'METHOD_SELECTION';

const STAGE_ORDER: WorkflowStage[] = [
  'METHOD_SELECTION',
  'DEVICE_SELECTION',
  'FACE_CAPTURE',
  'FACE_PROCESSING',
  'SELFIE_HANDOFF',
  'DOCUMENT_CONSENT',
  'DOCUMENT_CAPTURE',
  'DOCUMENT_PROCESSING',
];

const SCREEN_STAGE: Partial<Record<ScreenType, WorkflowStage>> = {
  main: 'METHOD_SELECTION',
  // Step-up explains a Portal-recorded upgrade requirement before capture; it
  // is a presentation of METHOD_SELECTION, never an external wait.
  'emblem-step-up': 'METHOD_SELECTION',
  'camera-access': 'DEVICE_SELECTION',
  'camera-setup': 'FACE_CAPTURE',
  'id-request': 'DOCUMENT_CONSENT',
  'id-capture': 'DOCUMENT_CAPTURE',
  'id-review': 'DOCUMENT_CAPTURE',
  'id-processing': 'DOCUMENT_PROCESSING',
};

const STAGE_SCREEN: Record<WorkflowStage, ScreenType> = {
  METHOD_SELECTION: 'main',
  DEVICE_SELECTION: 'camera-access',
  FACE_CAPTURE: 'camera-setup',
  FACE_PROCESSING: 'loading',
  SELFIE_HANDOFF: 'id-request',
  DOCUMENT_CONSENT: 'id-request',
  DOCUMENT_CAPTURE: 'id-capture',
  DOCUMENT_PROCESSING: 'id-processing',
  EXTERNAL_VERIFICATION: 'main',
};

/**
 * Stages Portal records no operation, deadline, or retry allowance for.
 * Portal accepts an ADVANCE that passes over one of them (today
 * METHOD_SELECTION straight to FACE_CAPTURE), so a flow already committed to
 * the stage beyond spends no command on it. Every other hop is walked one
 * validated stage at a time.
 */
const PRESENTATION_ONLY_STAGES = new Set<WorkflowStage>(['DEVICE_SELECTION']);

/** Upper bound on retained capture-preparation trail entries. */
const MAX_CAPTURE_PREPARATION_TRAIL = 8;

/**
 * Upper bound on any single lifecycle request, including its response body.
 * A request sent on a connection the browser has silently lost (a suspended
 * tab, a network switch) may otherwise never settle, and because reads are
 * coalesced and commands serialized, it would stall every later read and
 * command for the page. A timed-out request is a transport failure: the
 * existing bounded retry and fence-conflict handling decide what follows.
 */
export const LIFECYCLE_REQUEST_TIMEOUT_MS = 20_000;

const stageIndex = (stage: WorkflowStage): number => STAGE_ORDER.indexOf(stage);

const operationStageAfter = (index: number): WorkflowStage | null =>
  STAGE_ORDER.slice(index + 1).find((stage) => !PRESENTATION_ONLY_STAGES.has(stage)) ?? null;

const commandOptionsFor = (stage: WorkflowStage): Record<string, unknown> => {
  switch (stage) {
    case 'FACE_CAPTURE':
      return { operationKind: 'FACE_CAPTURE', operationStatus: 'IN_PROGRESS' };
    case 'FACE_PROCESSING':
      return { operationKind: 'FACE_CAPTURE', operationStatus: 'WAITING' };
    case 'SELFIE_HANDOFF':
      return { operationKind: 'SELFIE_ARTIFACT_FETCH', operationStatus: 'WAITING' };
    case 'DOCUMENT_CAPTURE':
      return { operationKind: 'DOCUMENT_CAPTURE', operationStatus: 'IN_PROGRESS' };
    case 'DOCUMENT_PROCESSING':
      return { operationKind: 'DOCUMENT_CAPTURE', operationStatus: 'WAITING' };
    default:
      return {};
  }
};

export interface CapturePreparationSummary {
  /** When the capture alignment was requested (navigation commit) or, absent that, when preparation began. */
  startedAt: number;
  /** Lifecycle requests that settled during the preparation window. */
  requestCount: number;
  /** Ordered, bounded labels of those requests, e.g. `start_attempt>advance:face_capture`. A trailing `!` marks a failed request. */
  trail: string;
}

/**
 * Portal accepted the face result, but the selfie handoff that follows it was
 * never established: the page reloaded or was suspended between the two
 * commands. Nothing on Portal will move it on, so the ID flow resumes it
 * rather than waiting.
 */
export const awaitsSelfieHandoff = (projection: LifecycleProjection): boolean =>
  projection.sessionStatus === 'PENDING' &&
  !projection.terminal &&
  projection.attempt?.status === 'ACTIVE' &&
  projection.attempt.stage === 'FACE_PROCESSING' &&
  projection.attempt.operation?.kind === 'FACE_CAPTURE' &&
  projection.attempt.operation.status === 'COMPLETED';

const isActiveSelfieHandoff = (projection: LifecycleProjection): boolean =>
  projection.sessionStatus === 'PENDING' &&
  !projection.terminal &&
  projection.attempt?.status === 'ACTIVE' &&
  projection.attempt.stage === 'SELFIE_HANDOFF' &&
  projection.attempt.operation?.kind === 'SELFIE_ARTIFACT_FETCH' &&
  (projection.attempt.operation.status === 'WAITING' ||
    projection.attempt.operation.status === 'IN_PROGRESS');

const hasReachedDocumentFlow = (
  projection: LifecycleProjection,
  attemptId: string,
  attemptGeneration: number,
): boolean => {
  if (
    projection.sessionStatus !== 'PENDING' ||
    projection.terminal ||
    projection.attempt?.status !== 'ACTIVE' ||
    projection.attempt.id !== attemptId ||
    projection.attempt.generation !== attemptGeneration
  ) {
    return false;
  }
  return stageIndex(projection.attempt.stage) >= stageIndex('DOCUMENT_CONSENT');
};

export class VerificationLifecycleService {
  private current: LifecycleProjection | null = null;
  /**
   * Whether `current` came from a response Portal accepted for this actor (a
   * 2xx read or command). A projection carried by a rejected request is still
   * retained for recovery and fencing, but a rejected request never leaves an
   * authorizing cache behind: only an accepted projection may gate capture or
   * decide that the first attempt exists.
   */
  private currentAccepted = false;
  /**
   * Whether Portal has accepted a response for this actor since the camera
   * was last authorized in this page. Each camera authorization consumes it,
   * so a re-entry that saw no newer accepted response performs one confirming
   * read. Normal entry always satisfies it through the alignment command's
   * own response.
   */
  private acceptedSinceCaptureAuthorization = false;
  /** Open capture-preparation window, or the most recently closed one. */
  private capturePreparation: { startedAt: number; trail: string[]; open: boolean } | null = null;
  private mutationTail: Promise<void> = Promise.resolve();
  private alignmentTail: Promise<void> = Promise.resolve();
  private readonly pendingAlignments = new Map<WorkflowStage, Promise<void>>();
  private reconciliation: Promise<LifecycleProjection> | null = null;
  private reconciliationSessionId: string | null = null;
  private reconciliationController: AbortController | null = null;
  private recovery: Promise<LifecycleProjection> | null = null;
  private readonly failureReports = new Map<string, Promise<LifecycleProjection>>();
  private rateLimit: LifecycleRateLimitError | null = null;
  private ownershipGeneration = 0;
  private settledRequestCount = 0;

  getCurrent(): LifecycleProjection | null {
    return this.current;
  }

  /**
   * Number of lifecycle HTTP requests that have settled so far. Diagnostics
   * only: callers snapshot it around prerequisite work to report how many
   * sequential Portal round trips gated that work.
   */
  getRequestCount(): number {
    return this.settledRequestCount;
  }

  /**
   * Summary of the most recent capture preparation: the lifecycle requests
   * that settled between the capture alignment being requested (navigation
   * commit) and the camera being authorized. Diagnostics only.
   */
  lastCapturePreparation(): CapturePreparationSummary | null {
    const preparation = this.capturePreparation;
    if (!preparation) return null;
    return {
      startedAt: preparation.startedAt,
      requestCount: preparation.trail.length,
      trail: preparation.trail.join('>'),
    };
  }

  /**
   * Stop treating the retained projection as authorization. Used when this
   * actor learns outside the lifecycle transport that ownership or state may
   * have moved (for example, becoming the desktop observer of a mobile
   * claim). Responses still in flight for the previous ownership are
   * discarded, and the next gate or start decision reads fresh state.
   */
  markProjectionUnconfirmed(): void {
    this.ownershipGeneration += 1;
    this.reconciliationController?.abort();
    this.reconciliationController = null;
    this.reconciliation = null;
    this.currentAccepted = false;
    this.acceptedSinceCaptureAuthorization = false;
  }

  reset(): void {
    this.ownershipGeneration += 1;
    this.reconciliationController?.abort();
    this.reconciliationController = null;
    this.reconciliation = null;
    this.reconciliationSessionId = null;
    this.recovery = null;
    this.rateLimit = null;
    this.mutationTail = Promise.resolve();
    this.alignmentTail = Promise.resolve();
    this.failureReports.clear();
    this.pendingAlignments.clear();
    this.current = null;
    this.currentAccepted = false;
    this.acceptedSinceCaptureAuthorization = false;
    this.capturePreparation = null;
  }

  async reconcile(timeoutMs?: number): Promise<LifecycleProjection> {
    const config = await runtimeConfig.loadConfig();
    if (!config.sessionId || !config.sessionToken) {
      throw new Error('Session context is unavailable');
    }

    if (
      this.reconciliation &&
      this.reconciliationSessionId === config.sessionId
    ) {
      // Settlement recovery may join an existing read. Its bounded allowance
      // must also cancel that read so a stalled promise cannot pin recovery.
      const controller = this.reconciliationController;
      const timer = timeoutMs === undefined ? undefined
        : setTimeout(() => controller?.abort(), timeoutMs);
      try {
        return await this.reconciliation;
      } finally {
        clearTimeout(timer);
      }
    }

    if (this.reconciliationSessionId !== config.sessionId) {
      if (this.reconciliationSessionId !== null || this.current !== null) {
        this.ownershipGeneration += 1;
      }
      this.reconciliationController?.abort();
      this.current = null;
      this.currentAccepted = false;
      this.acceptedSinceCaptureAuthorization = false;
      this.capturePreparation = null;
      this.failureReports.clear();
    }

    const controller = new AbortController();
    const timer = timeoutMs === undefined ? undefined
      : setTimeout(() => controller.abort(), timeoutMs);
    const sessionId = config.sessionId;
    const ownershipGeneration = this.ownershipGeneration;
    const run = this.request(
      sessionId,
      config.sessionToken,
      'GET',
      undefined,
      controller.signal,
    ).then((response) => {
      if (
        this.reconciliationSessionId === sessionId &&
        this.ownershipGeneration === ownershipGeneration
      ) {
        return this.acceptProjection(response);
      }
      throw new LifecycleOwnershipChanged();
    }, (error: unknown) => {
      if (
        this.reconciliationSessionId === sessionId &&
        this.ownershipGeneration === ownershipGeneration
      ) {
        // This actor's read did not succeed, whether Portal rejected it (for
        // example ACTOR_NO_LONGER_CURRENT after a mobile claim) or transport
        // failed. Whatever was accepted earlier no longer authorizes anything
        // until a read succeeds.
        this.currentAccepted = false;
      }
      throw error;
    }).finally(() => {
      clearTimeout(timer);
      if (this.reconciliation === run) {
        this.reconciliation = null;
        this.reconciliationController = null;
      }
    });

    this.reconciliationSessionId = sessionId;
    this.reconciliationController = controller;
    this.reconciliation = run;
    return run;
  }

  /**
   * Ensure the pending session has its first durable attempt.
   *
   * The decision is made on the projection Portal most recently accepted for
   * this actor (bootstrap reconciliation or a command response); it does not
   * issue another read first. A projection carried by a rejected request
   * never serves as that basis, and a reconciliation that fails while awaited
   * propagates its failure. START_ATTEMPT is version-fenced, so a stale
   * projection yields Portal's current one instead of a wrong mutation.
   * Commands are serialized, and a START_ATTEMPT that reaches the front of
   * the queue after an earlier one already established the attempt returns
   * that projection without a request, so concurrent callers never issue a
   * duplicate that Portal would only replay.
   */
  async startIfNeeded(): Promise<LifecycleProjection> {
    let lifecycle =
      (await this.cachedProjectionForCurrentSession()) ?? (await this.reconcile());
    if (lifecycle.sessionStatus === 'PENDING' && !lifecycle.attempt) {
      lifecycle = await this.command('START_ATTEMPT');
    }
    return lifecycle;
  }

  /**
   * Retain a response Portal accepted for this actor. A response that lands
   * behind a newer projection already retained for the same session (a late
   * read behind a command, or the reverse) never rolls that state back; the
   * newer projection is returned instead so callers act on the freshest
   * authoritative state known.
   */
  private acceptProjection(response: LifecycleProjection): LifecycleProjection {
    const retained = this.current;
    if (
      retained &&
      retained.sessionId === response.sessionId &&
      retained.lifecycleVersion > response.lifecycleVersion
    ) {
      lifecycleLogger.debug('Ignored a lifecycle response older than the retained projection', {
        labels: {
          responseVersion: `${response.lifecycleVersion}`,
          retainedVersion: `${retained.lifecycleVersion}`,
        },
      });
      return retained;
    }
    this.current = response;
    this.currentAccepted = true;
    this.acceptedSinceCaptureAuthorization = true;
    if (response.sessionStatus !== 'PENDING') {
      // A finished session needs no captured selfie.
      clearBestSelfie();
    }
    return response;
  }

  /**
   * The projection Portal most recently accepted for this actor and the
   * configured session, or null when no accepted projection is held. An
   * in-flight reconciliation for the same session is returned as-is so its
   * fresher result is used and its failure propagates to the caller; a
   * projection carried by a rejected request never qualifies.
   */
  private async cachedProjectionForCurrentSession(): Promise<LifecycleProjection | null> {
    const config = await runtimeConfig.loadConfig();
    if (!config.sessionId || !config.sessionToken) {
      throw new Error('Session context is unavailable');
    }
    if (this.reconciliation && this.reconciliationSessionId === config.sessionId) {
      return this.reconciliation;
    }
    return this.currentAccepted && this.current?.sessionId === config.sessionId
      ? this.current
      : null;
  }

  screenFor(projection: LifecycleProjection): ScreenType {
    if (projection.sessionStatus === 'VERIFIED') return 'success';
    if (projection.sessionStatus !== 'PENDING') return 'failure';
    // After a reload, keep waiting for a result this browser already received
    // while Portal can still record it, rather than offering another capture.
    if (awaitsRememberedResult(projection)) return 'success';
    // The selfie retry screen belongs only to the face-match failure itself;
    // failures inside a recovery keep their own recovery screens.
    if (
      projection.faceMatchRecovery === 'SELFIE_RETRY_OFFERED' &&
      projection.recovery.action === 'RETRY_OPERATION'
    ) {
      return 'selfie-retry';
    }
    if (
      isFaceOnlyComparison(projection) &&
      (projection.recovery.action === 'WAIT' || projection.recovery.action === 'CONTINUE')
    ) {
      return 'id-processing';
    }
    if (
      isActiveSelfieHandoff(projection) &&
      (projection.recovery.action === 'WAIT' ||
        projection.recovery.action === 'CONTINUE')
    ) {
      return 'id-request';
    }
    if (awaitsSelfieHandoff(projection)) return 'id-request';
    if (projection.recovery.action === 'START_NEW_SESSION' ||
        projection.recovery.action === 'START_SUCCESSOR_ATTEMPT' ||
        projection.recovery.action === 'RETRY_OPERATION' ||
        projection.recovery.action === 'WAIT') return 'failure';
    if (isEmblemUpgradeProjection(projection)) return 'emblem-step-up';
    return STAGE_SCREEN[projection.recovery.targetStage || projection.attempt?.stage || 'METHOD_SELECTION'];
  }

  async alignToScreen(screen: ScreenType): Promise<void> {
    const target = SCREEN_STAGE[screen];
    if (!target) return;
    return this.alignToStage(target, screen);
  }

  async alignToStage(
    target: WorkflowStage,
    requestedScreen: ScreenType = STAGE_SCREEN[target],
  ): Promise<void> {
    const pending = this.pendingAlignments.get(target);
    if (pending) return pending;
    if (target === 'FACE_CAPTURE') this.beginCapturePreparation();

    const run = this.alignmentTail
      .catch(() => undefined)
      .then(() => this.alignToTarget(target, requestedScreen));
    this.pendingAlignments.set(target, run);
    this.alignmentTail = run.then(
      () => undefined,
      () => undefined,
    );
    const clearPending = () => {
      if (this.pendingAlignments.get(target) === run) {
        this.pendingAlignments.delete(target);
      }
    };
    void run.then(clearPending, clearPending);
    return run;
  }

  /**
   * Report the accepted engine fact and establish the Portal-owned selfie
   * handoff operation. This is deliberately separate from route alignment:
   * an engine result may complete a valid WAITING face operation, while
   * generic alignment must stop when Portal says to wait.
   */
  async acceptFaceCaptureResultForHandoff(): Promise<LifecycleProjection> {
    let lifecycle = this.current || await this.reconcile();
    if (
      lifecycle.sessionStatus !== 'PENDING' ||
      lifecycle.terminal ||
      !lifecycle.attempt ||
      lifecycle.attempt.status !== 'ACTIVE'
    ) {
      throw new LifecycleProjectionUnavailable(lifecycle);
    }

    if (lifecycle.attempt.stage === 'FACE_CAPTURE') {
      const priorVersion = lifecycle.lifecycleVersion;
      const priorAttemptVersion = lifecycle.attempt.version;
      try {
        lifecycle = await this.command('ADVANCE', {
          targetStage: 'FACE_PROCESSING',
          operationKind: 'FACE_CAPTURE',
          operationStatus: 'COMPLETED',
        }, true);
      } catch (error) {
        if (error instanceof LifecycleConflict) {
          throw new LifecycleAlignmentStopped(
            error.code || 'LIFECYCLE_CONFLICT',
            error.lifecycle || lifecycle,
          );
        }
        throw error;
      }
      if (
        lifecycle.attempt?.stage !== 'FACE_PROCESSING' ||
        lifecycle.lifecycleVersion <= priorVersion ||
        lifecycle.attempt.version <= priorAttemptVersion
      ) {
        throw new LifecycleAlignmentStopped('NO_LIFECYCLE_PROGRESS', lifecycle);
      }
    }

    if (
      lifecycle.attempt?.stage !== 'FACE_PROCESSING' ||
      lifecycle.attempt.operation?.kind !== 'FACE_CAPTURE'
    ) {
      if (lifecycle.attempt?.stage === 'SELFIE_HANDOFF') return lifecycle;
      throw new LifecycleProjectionUnavailable(lifecycle);
    }

    const priorVersion = lifecycle.lifecycleVersion;
    const priorAttemptVersion = lifecycle.attempt.version;
    try {
      lifecycle = await this.command('ADVANCE', {
        targetStage: 'SELFIE_HANDOFF',
        operationKind: 'SELFIE_ARTIFACT_FETCH',
        operationStatus: 'WAITING',
      }, true);
    } catch (error) {
      if (error instanceof LifecycleConflict) {
        throw new LifecycleAlignmentStopped(
          error.code || 'LIFECYCLE_CONFLICT',
          error.lifecycle || lifecycle,
        );
      }
      throw error;
    }
    if (
      lifecycle.attempt?.stage !== 'SELFIE_HANDOFF' ||
      lifecycle.lifecycleVersion <= priorVersion ||
      lifecycle.attempt.version <= priorAttemptVersion
    ) {
      throw new LifecycleAlignmentStopped('NO_LIFECYCLE_PROGRESS', lifecycle);
    }
    return lifecycle;
  }

  /**
   * Report that the operation-scoped selfie artifact was successfully read.
   * This is the sole path that completes SELFIE_HANDOFF; generic route
   * alignment must continue to stop on WAIT.
   */
  async completeSelfieHandoff(): Promise<LifecycleProjection> {
    const retained = this.current;
    let lifecycle = retained || await this.reconcile();
    // The handoff deadline is short, and a device clock running ahead of
    // Portal made it look elapsed before any request was sent. As for capture,
    // the browser clock only selects a fresh read; Portal applies the deadline
    // before it projects, and it refuses a late command itself.
    const deadlineAt = lifecycle.attempt?.operation?.deadlineAt;
    if (retained && deadlineAt && Date.parse(deadlineAt) <= Date.now()) {
      lifecycle = await this.reconcile();
    }
    const attempt = lifecycle.attempt;
    if (!attempt) {
      throw new LifecycleProjectionUnavailable(lifecycle);
    }

    const attemptId = attempt.id;
    const attemptGeneration = attempt.generation;
    if (hasReachedDocumentFlow(lifecycle, attemptId, attemptGeneration)) {
      return lifecycle;
    }

    const operation = attempt.operation;
    if (
      !isActiveSelfieHandoff(lifecycle) ||
      !operation?.deadlineAt ||
      (lifecycle.recovery.action !== 'WAIT' &&
        lifecycle.recovery.action !== 'CONTINUE')
    ) {
      throw new LifecycleProjectionUnavailable(lifecycle);
    }

    const priorVersion = lifecycle.lifecycleVersion;
    const priorAttemptVersion = attempt.version;
    // In a face-match selfie retry the new selfie is compared directly with
    // the ID Verify Engine retained; consent and document capture are done.
    const selfieRetry = isFaceMatchSelfieRetry(lifecycle);
    const targetStage: WorkflowStage = selfieRetry ? 'DOCUMENT_PROCESSING' : 'DOCUMENT_CONSENT';
    let completed: LifecycleProjection;
    try {
      completed = await this.command('ADVANCE', selfieRetry
        ? {
            targetStage,
            operationKind: 'DOCUMENT_PROCESSING',
            operationStatus: 'WAITING',
          }
        : {
            targetStage,
            operationKind: 'SELFIE_ARTIFACT_FETCH',
            operationStatus: 'COMPLETED',
          }, true);
    } catch (error) {
      if (
        error instanceof LifecycleConflict &&
        error.lifecycle &&
        hasReachedDocumentFlow(error.lifecycle, attemptId, attemptGeneration)
      ) {
        return error.lifecycle;
      }
      if (error instanceof LifecycleConflict) {
        throw new LifecycleAlignmentStopped(
          error.code || 'LIFECYCLE_CONFLICT',
          error.lifecycle || lifecycle,
        );
      }
      throw error;
    }

    if (
      completed.attempt?.stage !== targetStage ||
      completed.attempt.id !== attemptId ||
      completed.attempt.generation !== attemptGeneration ||
      completed.lifecycleVersion <= priorVersion ||
      completed.attempt.version <= priorAttemptVersion
    ) {
      throw new LifecycleAlignmentStopped('NO_LIFECYCLE_PROGRESS', completed);
    }
    return completed;
  }

  /**
   * Wait for the single flow-level alignment owner, then return the
   * authoritative projection that fences capture and transport work. This
   * method never advances lifecycle state itself.
   *
   * The alignment commands already return Portal's projection of the state
   * they established, so that projection is used directly when it authorizes
   * the requested screen. A separate read is issued only when no usable
   * projection exists for the session, when the observed one does not
   * authorize the screen (every refusal is decided on fresh server state), or
   * when the caller explicitly requires a fresh read.
   */
  async projectionAfterAlignment(
    screen: ScreenType,
    options: {
      requireFreshProjection?: boolean;
      /**
       * Use the retained projection only if Portal accepted a response since
       * the camera was last authorized; otherwise read fresh state. Evaluated
       * after the alignment owner has finished, so an alignment command's own
       * response satisfies it.
       */
      requireAcceptanceSinceCaptureAuthorization?: boolean;
    } = {},
  ): Promise<LifecycleProjection> {
    await this.alignmentTail;
    const retainedIsRecentEnough =
      options.requireAcceptanceSinceCaptureAuthorization !== true ||
      this.acceptedSinceCaptureAuthorization;
    if (options.requireFreshProjection !== true && retainedIsRecentEnough) {
      const observed = await this.cachedProjectionForCurrentSession();
      if (
        observed &&
        Date.parse(observed.expiresAt) > Date.now() &&
        this.projectionGateFailure(observed, screen, 'retained') === null
      ) {
        return observed;
      }
    }

    const lifecycle = await this.reconcile();
    const failure = this.projectionGateFailure(lifecycle, screen, 'fresh');
    if (failure) throw failure;
    return lifecycle;
  }

  /**
   * Decide whether a projection authorizes work on the requested screen.
   * Returns the error to raise, or null when the projection is acceptable.
   *
   * Portal applies session expiry and the operation deadline before it
   * projects, so a fresh response is trusted as it stands. Only a retained
   * projection is additionally checked against the browser clock, and that
   * check merely selects a fresh read; it never refuses on its own.
   */
  private projectionGateFailure(
    lifecycle: LifecycleProjection,
    screen: ScreenType,
    source: 'retained' | 'fresh',
  ): LifecycleProjectionUnavailable | LifecycleAlignmentPending | null {
    const target = SCREEN_STAGE[screen];
    if (!target) return null;
    if (lifecycle.terminal || !lifecycle.attempt) {
      if (screen === 'camera-setup') {
        return new LifecycleProjectionUnavailable(lifecycle);
      }
      return null;
    }

    if (
      lifecycle.sessionStatus !== 'PENDING' ||
      lifecycle.attempt.status !== 'ACTIVE' ||
      lifecycle.recovery.action !== 'CONTINUE'
    ) {
      return new LifecycleProjectionUnavailable(lifecycle);
    }

    const currentIndex = stageIndex(lifecycle.attempt.stage);
    const targetIndex = stageIndex(target);
    if (targetIndex >= 0 && currentIndex >= 0 && currentIndex < targetIndex) {
      if (screen === 'camera-setup') {
        return new LifecycleProjectionUnavailable(lifecycle);
      }
      return new LifecycleAlignmentPending(screen, lifecycle.attempt.stage);
    }

    if (screen === 'camera-setup') {
      const operation = lifecycle.attempt.operation;
      const deadlineElapsed =
        source === 'retained' && operation?.deadlineAt
          ? Date.parse(operation.deadlineAt) <= Date.now()
          : false;
      if (
        lifecycle.attempt.stage !== 'FACE_CAPTURE' ||
        operation?.kind !== 'FACE_CAPTURE' ||
        operation.status !== 'IN_PROGRESS' ||
        deadlineElapsed
      ) {
        return new LifecycleProjectionUnavailable(lifecycle);
      }
    }
    return null;
  }

  /**
   * Prepare the authoritative FACE_CAPTURE operation before camera access.
   *
   * projectionAfterAlignment remains the single client-side validity gate for
   * capture work. Initial mount and reload act on the projection Portal
   * returned for the alignment that established the face operation, or for
   * the initialization read. Camera activation additionally requires that
   * Portal accepted that projection after the previous camera authorization
   * in this page: a re-entry that saw no newer accepted response performs one
   * confirming read first, while normal entry pays nothing because the
   * alignment command's own response is that acceptance. An explicit camera
   * retry always reads fresh state first, then may ask Portal to perform its
   * already-selected FACE_CAPTURE recovery, after which the same gate
   * confirms the replacement operation before getUserMedia runs.
   */
  async prepareCameraInitialization(options: {
    allowFaceCaptureRecovery?: boolean;
  } = {}): Promise<LifecycleProjection> {
    if (!this.capturePreparation?.open) this.beginCapturePreparation();
    try {
      const authorized = await this.confirmCapturePreparation(options);
      // Every authorization consumes its acceptance: the next one needs a
      // response Portal accepted after this point.
      this.acceptedSinceCaptureAuthorization = false;
      return authorized;
    } finally {
      if (this.capturePreparation) this.capturePreparation.open = false;
    }
  }

  private beginCapturePreparation(): void {
    this.capturePreparation = { startedAt: Date.now(), trail: [], open: true };
  }

  private async confirmCapturePreparation(options: {
    allowFaceCaptureRecovery?: boolean;
  }): Promise<LifecycleProjection> {
    try {
      return await this.projectionAfterAlignment('camera-setup', {
        requireFreshProjection: options.allowFaceCaptureRecovery === true,
        requireAcceptanceSinceCaptureAuthorization: true,
      });
    } catch (error) {
      if (!(error instanceof LifecycleProjectionUnavailable)) {
        throw error;
      }

      const lifecycle = error.lifecycle;
      if (
        options.allowFaceCaptureRecovery !== true ||
        lifecycle.recovery.action !== 'RETRY_OPERATION' ||
        lifecycle.recovery.targetStage !== 'FACE_CAPTURE'
      ) {
        throw error;
      }

      await this.performRecovery({
        expectedAction: 'RETRY_OPERATION',
        expectedTargetStage: 'FACE_CAPTURE',
      });
      return this.projectionAfterAlignment('camera-setup', {
        requireAcceptanceSinceCaptureAuthorization: true,
      });
    }
  }

  private async alignToTarget(
    target: WorkflowStage,
    requestedScreen: ScreenType,
  ): Promise<void> {
    let lifecycle = this.current || await this.startIfNeeded();
    if (lifecycle.sessionStatus === 'PENDING' && !lifecycle.attempt) {
      // A prior bootstrap reconciliation may have cached the pre-attempt
      // projection. Alignment owns starting that first attempt before it can
      // advance to the requested screen.
      lifecycle = await this.startIfNeeded();
    }
    if (!lifecycle.attempt || lifecycle.terminal || lifecycle.attempt.stage === target) return;
    if (lifecycle.recovery.action !== 'CONTINUE') {
      throw new LifecycleAlignmentStopped(
        `RECOVERY_${lifecycle.recovery.action}`,
        lifecycle,
      );
    }

    let currentIndex = stageIndex(lifecycle.attempt.stage);
    const targetIndex = stageIndex(target);
    if (currentIndex < 0 || targetIndex < currentIndex) {
      lifecycleLogger.info('Browser route projected behind authoritative stage', {
        labels: { requestedScreen, authoritativeStage: lifecycle.attempt.stage },
      });
      return;
    }

    // A presentation-only stage owns no operation, deadline, or retry
    // allowance, so passing over it costs the workflow nothing. When the flow
    // has already committed to a stage beyond it, this alignment continues to
    // the next operation-bearing stage instead of spending a command on the
    // presentation-only one. Unlike skipping the work, Portal is still at or
    // past the stage the caller asked for when this resolves, so callers that
    // await alignment keep that guarantee.
    const finalTarget =
      PRESENTATION_ONLY_STAGES.has(target) && this.hasPendingAlignmentBeyond(target)
        ? operationStageAfter(targetIndex) ?? target
        : target;
    const finalIndex = stageIndex(finalTarget);
    if (finalTarget !== target) {
      lifecycleLogger.debug('Passing over a presentation-only stage to a committed destination', {
        labels: { requestedScreen, target, finalTarget },
      });
    }
    // The hops Portal is asked for, in order. Presentation-only stages are
    // passed over unless one is the final target itself.
    const hopsFrom = (fromIndex: number, passOverPresentationOnly: boolean): WorkflowStage[] =>
      STAGE_ORDER.slice(fromIndex + 1, finalIndex + 1).filter(
        (stage) =>
          stage === finalTarget ||
          !passOverPresentationOnly ||
          !PRESENTATION_ONLY_STAGES.has(stage),
      );

    let hops = hopsFrom(currentIndex, true);
    let singleStageFallbackApplied = false;
    let advances = 0;
    while (hops.length > 0) {
      if (advances >= STAGE_ORDER.length) {
        // Defensive bound against an implementation defect; not workflow logic.
        throw new LifecycleAlignmentStopped('ALIGNMENT_BOUND_EXHAUSTED', lifecycle);
      }
      const currentStage = STAGE_ORDER[currentIndex];
      const nextStage = hops[0];
      const passesOverStage = stageIndex(nextStage) > currentIndex + 1;
      const priorVersion = lifecycle.lifecycleVersion;
      if (!lifecycle.attempt) {
        throw new LifecycleAlignmentStopped('ATTEMPT_UNAVAILABLE', lifecycle);
      }
      const priorAttemptVersion = lifecycle.attempt.version;
      try {
        lifecycle = await this.command(
          'ADVANCE',
          { targetStage: nextStage, ...commandOptionsFor(nextStage) },
          true,
        );
      } catch (error) {
        if (error instanceof LifecycleConflict) {
          if (
            passesOverStage &&
            !singleStageFallbackApplied &&
            error.code === 'RECOVERY_ACTION_NOT_AVAILABLE' &&
            error.lifecycle?.lifecycleVersion === priorVersion &&
            error.lifecycle.attempt?.stage === currentStage
          ) {
            // Mixed-revision compatibility: a Portal revision that predates
            // the direct transition rejected it without changing state. Walk
            // the remaining distance one stage at a time instead.
            lifecycleLogger.info('Direct stage transition unavailable; aligning one stage at a time', {
              labels: { requestedScreen, from: currentStage, to: nextStage },
            });
            singleStageFallbackApplied = true;
            lifecycle = error.lifecycle;
            hops = hopsFrom(currentIndex, false);
            continue;
          }
          throw new LifecycleAlignmentStopped(
            error.code || 'LIFECYCLE_CONFLICT',
            error.lifecycle || lifecycle,
          );
        }
        throw error;
      }

      const reachedStage = lifecycle.attempt?.stage;
      const madeProgress =
        reachedStage === nextStage &&
        lifecycle.lifecycleVersion > priorVersion &&
        (lifecycle.attempt?.version ?? -1) > priorAttemptVersion;
      if (!madeProgress) {
        throw new LifecycleAlignmentStopped('NO_LIFECYCLE_PROGRESS', lifecycle);
      }

      advances += 1;
      currentIndex = stageIndex(nextStage);
      hops = hops.slice(1);
      if (lifecycle.recovery.action !== 'CONTINUE') {
        if (currentIndex >= targetIndex) return;
        throw new LifecycleAlignmentStopped(
          `RECOVERY_${lifecycle.recovery.action}`,
          lifecycle,
        );
      }
    }
  }

  private hasPendingAlignmentBeyond(target: WorkflowStage): boolean {
    const position = stageIndex(target);
    for (const pendingTarget of this.pendingAlignments.keys()) {
      if (stageIndex(pendingTarget) > position) return true;
    }
    return false;
  }

  async performRecovery(options: {
    expectedAction?: RecoveryAction;
    expectedTargetStage?: WorkflowStage;
  } = {}): Promise<LifecycleProjection> {
    if (this.recovery) return this.recovery;
    const run = this.executeRecovery(options).finally(() => {
      if (this.recovery === run) this.recovery = null;
    });
    this.recovery = run;
    return run;
  }

  private async executeRecovery(options: {
    expectedAction?: RecoveryAction;
    expectedTargetStage?: WorkflowStage;
  }): Promise<LifecycleProjection> {
    const lifecycle = await this.reconcile();
    if (
      (options.expectedAction &&
        lifecycle.recovery.action !== options.expectedAction) ||
      (options.expectedTargetStage &&
        lifecycle.recovery.targetStage !== options.expectedTargetStage)
    ) {
      throw new LifecycleProjectionUnavailable(lifecycle);
    }
    switch (lifecycle.recovery.action) {
      case 'RETRY_OPERATION':
        return this.command(
          'RETRY_OPERATION',
          {},
          false,
          operationFenceFromProjection(lifecycle) || undefined,
        );
      case 'START_SUCCESSOR_ATTEMPT':
        return this.command('START_SUCCESSOR_ATTEMPT');
      case 'START_NEW_SESSION':
        return this.command('START_NEW_SESSION');
      case 'CONTINUE':
      case 'WAIT':
      case 'RETURN':
      case 'NONE':
        return lifecycle;
    }
  }

  /**
   * Instead of the offered selfie, verify with a different ID. Portal permits
   * this choice only while a face-match selfie retry is offered.
   */
  async useDifferentIdInsteadOfSelfie(): Promise<LifecycleProjection> {
    const lifecycle = await this.reconcile();
    if (
      lifecycle.faceMatchRecovery !== 'SELFIE_RETRY_OFFERED' ||
      lifecycle.recovery.action !== 'RETRY_OPERATION'
    ) {
      throw new LifecycleProjectionUnavailable(lifecycle);
    }
    return this.command(
      'RETRY_OPERATION',
      { targetStage: 'DOCUMENT_CAPTURE' },
      false,
      operationFenceFromProjection(lifecycle) || undefined,
    );
  }

  async reportOperationFailureOnce(params: {
    failureCode: string;
    failureClass?: string;
    source?: LifecycleOperationFence | null;
  }): Promise<LifecycleProjection> {
    const lifecycle = this.current || await this.startIfNeeded();
    if (
      lifecycle.sessionStatus !== 'PENDING' ||
      (!params.source && (
        !lifecycle.attempt?.operation ||
        lifecycle.attempt.operation.status === 'RETRYABLE_FAILURE'
      ))
    ) {
      return lifecycle;
    }
    const source = params.source || operationFenceFromProjection(lifecycle);
    if (!source || source.sessionId !== lifecycle.sessionId) {
      return lifecycle;
    }
    const key = [
      source.sessionId,
      source.attemptId,
      source.attemptGeneration,
      source.operationId,
      source.operationGeneration,
      params.failureCode,
    ].join(':');
    const pending = this.failureReports.get(key);
    if (pending) return pending;

    const run = this.command('REPORT_OPERATION_FAILURE', {
      failureCode: params.failureCode,
      ...(params.failureClass ? { failureClass: params.failureClass } : {}),
    }, false, source).catch((error) => {
      this.failureReports.delete(key);
      throw error;
    });
    this.failureReports.set(key, run);
    return run;
  }

  async retryOperationAfterFailure(params: {
    failureCode: string;
    failureClass?: string;
    source?: LifecycleOperationFence | null;
  }): Promise<LifecycleProjection> {
    const failed = await this.reportOperationFailureOnce({
      failureCode: params.failureCode,
      failureClass: params.failureClass,
      source: params.source,
    });
    if (failed.recovery.action !== 'RETRY_OPERATION') {
      return failed;
    }
    if (params.source && !projectionOwnsFence(failed, params.source)) {
      return failed;
    }
    return this.command(
      'RETRY_OPERATION',
      {},
      false,
      operationFenceFromProjection(failed) || undefined,
    );
  }

  async recoverOperationFailure(params: {
    failureCode: string;
    failureClass?: string;
    source?: LifecycleOperationFence | null;
  }): Promise<LifecycleProjection> {
    let lifecycle = await this.reconcile();
    if (lifecycle.sessionStatus !== 'PENDING' || !lifecycle.attempt) {
      return lifecycle;
    }

    if (
      lifecycle.attempt.operation &&
      lifecycle.attempt.operation.status !== 'RETRYABLE_FAILURE'
    ) {
      lifecycle = await this.reportOperationFailureOnce({
        failureCode: params.failureCode,
        failureClass: params.failureClass,
        source: params.source,
      });
    }

    switch (lifecycle.recovery.action) {
      case 'RETRY_OPERATION':
        if (params.source && !projectionOwnsFence(lifecycle, params.source)) {
          return lifecycle;
        }
        return this.command(
          'RETRY_OPERATION',
          {},
          false,
          operationFenceFromProjection(lifecycle) || undefined,
        );
      case 'START_SUCCESSOR_ATTEMPT':
        return this.command('START_SUCCESSOR_ATTEMPT');
      case 'START_NEW_SESSION':
        return this.command('START_NEW_SESSION');
      case 'CONTINUE':
      case 'WAIT':
      case 'RETURN':
      case 'NONE':
        return lifecycle;
    }
  }

  async cancel(): Promise<LifecycleProjection> {
    return this.command('CANCEL');
  }

  /**
   * Engine reports every unsuccessful Portal session-token validation as a
   * credential rejection, including Portal 5xx responses and timeouts. Before
   * a caller treats such a rejection as an authorization failure, Portal's
   * own authenticated read of the same bearer confirms it. `accepted` carries
   * the projection Portal served; `unavailable` means the read did not
   * establish either answer.
   */
  async confirmCredentialRejection(timeoutMs: number): Promise<
    | { kind: 'rejected' }
    | { kind: 'accepted'; lifecycle: LifecycleProjection }
    | { kind: 'unavailable' }
  > {
    try {
      return { kind: 'accepted', lifecycle: await this.reconcile(timeoutMs) };
    } catch (error) {
      return isDefinitiveLifecycleRejection(error)
        ? { kind: 'rejected' }
        : { kind: 'unavailable' };
    }
  }

  private async command(
    type: CommandType,
    extra: Record<string, unknown> = {},
    throwOnConflict = false,
    source?: LifecycleOperationFence,
  ): Promise<LifecycleProjection> {
    const run = this.mutationTail
      .catch(() => undefined)
      .then(() => this.executeCommand(type, extra, throwOnConflict, source));
    this.mutationTail = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private async executeCommand(
    type: CommandType,
    extra: Record<string, unknown>,
    throwOnConflict: boolean,
    source?: LifecycleOperationFence,
  ): Promise<LifecycleProjection> {
    const config = await runtimeConfig.loadConfig();
    if (!config.sessionId || !config.sessionToken) throw new Error('Session context is unavailable');
    const cached = this.current;
    // Fences may be taken from any retained projection, but whether the first
    // attempt already exists is decided only on an accepted one; otherwise a
    // fresh read precedes START_ATTEMPT exactly as before.
    const lifecycle =
      cached?.sessionId === config.sessionId &&
      (type !== 'START_ATTEMPT' || this.currentAccepted)
        ? cached
        : await this.reconcile();
    if (
      type === 'START_ATTEMPT' &&
      (lifecycle.attempt || lifecycle.sessionStatus !== 'PENDING')
    ) {
      // An earlier command in the queue already established the attempt (or
      // the session is no longer pending). Portal's START_ATTEMPT replay would
      // return exactly this projection, so the round trip is not issued.
      return lifecycle;
    }
    const ownershipGeneration = this.ownershipGeneration;
    const fencePayload = source
      ? {
          expectedLifecycleVersion: source.expectedLifecycleVersion,
          attemptId: source.attemptId,
          attemptGeneration: source.attemptGeneration,
          expectedAttemptVersion: source.expectedAttemptVersion,
          operationId: source.operationId,
          operationGeneration: source.operationGeneration,
        }
      : {
          expectedLifecycleVersion: lifecycle.lifecycleVersion,
          ...(lifecycle.attempt ? {
            attemptId: lifecycle.attempt.id,
            attemptGeneration: lifecycle.attempt.generation,
            expectedAttemptVersion: lifecycle.attempt.version,
            ...(lifecycle.attempt.operation ? {
              operationId: lifecycle.attempt.operation.id,
              operationGeneration: lifecycle.attempt.operation.generation,
            } : {}),
          } : {}),
        };
    const payload = {
      commandId: crypto.randomUUID(),
      type,
      ...extra,
      ...fencePayload,
    };
    try {
      const response = await this.request(config.sessionId, config.sessionToken, 'POST', payload);
      if (this.ownershipGeneration !== ownershipGeneration) {
        throw new LifecycleOwnershipChanged();
      }
      return this.acceptProjection(response);
    } catch (error) {
      if (error instanceof LifecycleOwnershipChanged) throw error;
      if (this.ownershipGeneration !== ownershipGeneration) {
        throw new LifecycleOwnershipChanged();
      }
      // A rejected request never leaves an authorizing cache behind.
      this.currentAccepted = false;
      if (error instanceof LifecycleConflict && error.lifecycle) {
        // Retained for recovery and fencing, never as authorization. A
        // conflict body older than the retained projection is not applied.
        if (
          !this.current ||
          this.current.sessionId !== error.lifecycle.sessionId ||
          this.current.lifecycleVersion <= error.lifecycle.lifecycleVersion
        ) {
          this.current = error.lifecycle;
        }
        if (throwOnConflict) throw error;
        return error.lifecycle;
      }
      throw error;
    }
  }

  private async request(
    sessionId: string,
    sessionToken: string,
    method: 'GET' | 'POST',
    body?: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<LifecycleProjection> {
    if (this.rateLimit) {
      if (this.rateLimit.retryAt > Date.now()) throw this.rateLimit;
      this.rateLimit = null;
    }
    const apiUrl = await runtimeConfig.getPortalApiUrl();
    const label = method === 'GET'
      ? 'read'
      : [body?.type, body?.targetStage]
          .filter((part): part is string => typeof part === 'string')
          .join(':')
          .toLowerCase();
    // The caller's signal still cancels the request; the bound applies either way.
    const controller = new AbortController();
    const abortFromCaller = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', abortFromCaller, { once: true });
    const timer = setTimeout(() => controller.abort(), LIFECYCLE_REQUEST_TIMEOUT_MS);
    try {
      return await this.send(
        `${apiUrl}/api/v1/sessions/${sessionId}/lifecycle${method === 'POST' ? '/commands' : ''}`,
        {
          method,
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            ...(body ? { 'Content-Type': 'application/json' } : {}),
          },
          ...(body ? { body: JSON.stringify(body) } : {}),
          signal: controller.signal,
        },
        label,
      );
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abortFromCaller);
    }
  }

  private async send(
    url: string,
    init: RequestInit & { signal: AbortSignal },
    label: string,
  ): Promise<LifecycleProjection> {
    let failed = true;
    let response: Response;
    try {
      response = await fetch(url, init);
      failed = !response.ok;
    } finally {
      this.settledRequestCount += 1;
      const preparation = this.capturePreparation;
      if (preparation?.open && preparation.trail.length < MAX_CAPTURE_PREPARATION_TRAIL) {
        preparation.trail.push(failed ? `${label}!` : label);
      }
    }
    const data = await response.json().catch((error: unknown) => {
      // A body cut off by the bound is a transport failure, not an answer.
      if (init.signal.aborted) throw error;
      return null;
    }) as LifecycleProjection | {
      code?: string;
      message?: string | { code?: string; message?: string };
      lifecycle?: LifecycleProjection;
      retryAfter?: number;
    } | null;
    if (response.status === 409) {
      const lifecycle = data && 'lifecycle' in data ? data.lifecycle : undefined;
      const nestedMessage = data && 'message' in data ? data.message : undefined;
      const code = data && 'code' in data && typeof data.code === 'string'
        ? data.code
        : typeof nestedMessage === 'object' && nestedMessage && typeof nestedMessage.code === 'string'
          ? nestedMessage.code
          : undefined;
      throw new LifecycleConflict(code, lifecycle);
    }
    if (!response.ok || !data || !('sessionId' in data)) {
      const nestedMessage = data && 'message' in data ? data.message : undefined;
      const code = data && 'code' in data && typeof data.code === 'string'
        ? data.code
        : typeof nestedMessage === 'object' && nestedMessage && typeof nestedMessage.code === 'string'
          ? nestedMessage.code
          : undefined;
      const message = typeof nestedMessage === 'string'
        ? nestedMessage
        : typeof nestedMessage === 'object' && nestedMessage && typeof nestedMessage.message === 'string'
          ? nestedMessage.message
          : `Lifecycle request failed (${response.status})`;
      if (response.status === 429) {
        const responseHeaders = response.headers as Headers | undefined;
        const retryAfterValue =
          responseHeaders?.get?.('Retry-After') ||
          responseHeaders?.get?.('X-RateLimit-Retry-After');
        const bodyRetryAfter = data && 'retryAfter' in data && typeof data.retryAfter === 'number'
          ? data.retryAfter
          : undefined;
        const rateLimit = new LifecycleRateLimitError(
          code,
          message,
          retryAtFrom(retryAfterValue, bodyRetryAfter),
        );
        this.rateLimit = rateLimit;
        throw rateLimit;
      }
      if (code === 'session_token_expired') void recoverTerminalReturn();
      throw new LifecycleRequestError(response.status, code, message);
    }
    this.rateLimit = null;
    return data;
  }
}

export class LifecycleConflict extends Error {
  constructor(
    readonly code?: string,
    readonly lifecycle?: LifecycleProjection,
  ) {
    super('Lifecycle state changed');
  }
}

export const getDesktopObserverRecoveryProjection = (
  error: unknown,
): LifecycleProjection | null => {
  if (!(error instanceof LifecycleConflict) || error.code !== 'ACTOR_NO_LONGER_CURRENT') {
    return null;
  }

  const lifecycle = error.lifecycle;
  if (
    !lifecycle ||
    lifecycle.sessionStatus !== 'PENDING' ||
    lifecycle.terminal ||
    lifecycle.actor?.role !== 'MOBILE_OWNER'
  ) {
    return null;
  }

  return lifecycle;
};

export class LifecycleAlignmentPending extends Error {
  constructor(
    readonly requestedScreen: ScreenType,
    readonly authoritativeStage: WorkflowStage,
  ) {
    super('Lifecycle alignment has not reached the requested screen');
    this.name = 'LifecycleAlignmentPending';
  }
}

export class LifecycleAlignmentStopped extends Error {
  constructor(
    readonly reason: string,
    readonly lifecycle: LifecycleProjection,
  ) {
    super(`Lifecycle alignment stopped: ${reason}`);
    this.name = 'LifecycleAlignmentStopped';
  }
}

export class LifecycleProjectionUnavailable extends Error {
  constructor(readonly lifecycle: LifecycleProjection) {
    super('The authoritative lifecycle does not permit this operation');
    this.name = 'LifecycleProjectionUnavailable';
  }
}

export class LifecycleOwnershipChanged extends Error {
  constructor() {
    super('Lifecycle request ownership changed');
    this.name = 'LifecycleOwnershipChanged';
  }
}

export class LifecycleRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

export class LifecycleRateLimitError extends LifecycleRequestError {
  constructor(
    code: string | undefined,
    message: string,
    readonly retryAt: number,
  ) {
    super(429, code, message);
    this.name = 'LifecycleRateLimitError';
  }
}

const retryAtFrom = (
  headerValue: string | null | undefined,
  bodySeconds: number | undefined,
): number => {
  const now = Date.now();
  if (headerValue) {
    const seconds = Number(headerValue);
    if (Number.isFinite(seconds) && seconds >= 0) {
      return now + seconds * 1000;
    }
    const date = Date.parse(headerValue);
    if (Number.isFinite(date) && date > now) return date;
  }
  if (typeof bodySeconds === 'number' && Number.isFinite(bodySeconds) && bodySeconds >= 0) {
    return now + bodySeconds * 1000;
  }
  return now + 60_000;
};

export const isExpiredLifecycleError = (error: unknown): boolean =>
  error instanceof LifecycleRequestError && error.code === 'session_token_expired';

export const isRateLimitedLifecycleError = (
  error: unknown,
): error is LifecycleRateLimitError => error instanceof LifecycleRateLimitError;

/**
 * Portal definitively refused this actor's lifecycle request: an expired,
 * invalid, forbidden or superseded bearer, or a missing session. 5xx, 408,
 * 429 and transport failures are never definitive.
 */
export const isDefinitiveLifecycleRejection = (error: unknown): boolean =>
  isExpiredLifecycleError(error) ||
  error instanceof LifecycleConflict ||
  (error instanceof LifecycleRequestError &&
    [401, 403, 404, 410].includes(error.status));

export const isTransientLifecycleError = (error: unknown): boolean => {
  if (error instanceof LifecycleOwnershipChanged) return false;
  if (error instanceof LifecycleRequestError) {
    return error.status === 408 || error.status >= 500;
  }
  return true;
};

export const verificationLifecycleService = new VerificationLifecycleService();
